import { createTheme } from '@mui/material/styles';

// 固定カラーテーマ（docs/color_palette.mdに基づく・視認性改善版）
const fixedColors = {
  primary: {
    main: '#ff9600', // 明るいオレンジ
    dark: '#cc7700', // 濃いオレンジ（ホバー状態・コントラスト改善）
    light: '#fff3e0', // 薄いオレンジ（背景用）
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#0066cc', // 青（リンク）
    dark: '#0052a3', // 濃い青
    light: '#e3f2fd', // 薄い青（背景用）
    contrastText: '#ffffff'
  },
  error: {
    main: '#e64c30', // 赤みがかったオレンジ（エラー/警告）
    dark: '#b93c27', // 濃い赤
    light: '#ffebee', // 薄い赤（背景用）
    contrastText: '#ffffff'
  },
  warning: {
    main: '#ff9600', // プライマリと同じオレンジ
    dark: '#cc7700', // 濃いオレンジ
    light: '#fff8e1', // 薄い黄色（背景用）
    contrastText: '#000000' // 明るい背景には黒文字
  },
  success: {
    main: '#4caf50', // 緑（成功メッセージ）
    dark: '#388e3c', // 濃い緑
    light: '#e8f5e9', // 薄い緑（背景用）
    contrastText: '#ffffff'
  },
  info: {
    main: '#2196f3', // 明るい青（情報メッセージ）
    dark: '#1976d2', // 濃い青
    light: '#e1f5fe', // 薄い青（背景用）
    contrastText: '#ffffff'
  },
  text: {
    primary: '#13131e', // 黒に近い色（メインテキスト）
    secondary: '#595960', // ダークグレー（サブテキスト）
    disabled: '#bdbdbd' // グレー（無効状態）
  },
  background: {
    default: '#fafafa', // 薄いグレー（メイン背景・コントラスト改善）
    paper: '#ffffff' // 白（カード等の背景）
  },
  divider: '#e0e0e0' // 薄いグレー（区切り線）
};

// カスタムカラーの追加
const customColors = {
  backgroundSecondary: '#f4f4f4', // ライトグレー（背景のバリエーション）
  backgroundWarm: '#eeece4', // オフホワイト（温かみのある背景）
  border: '#d6ceb4', // 明るい茶色がかったグレー（境界線）
  focus: 'rgba(255, 150, 0, 0.12)' // フォーカス状態の背景
};

const createAppTheme = () => {
  return createTheme({
    palette: fixedColors,
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"'
      ].join(','),
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
        lineHeight: 1.2
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
        lineHeight: 1.3
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
        lineHeight: 1.4
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.5
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.6
      },
      button: {
        textTransform: 'none',
        fontWeight: 500
      }
    },
    shape: {
      borderRadius: 8
    },
    spacing: 8,
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: '1rem',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }
          },
          containedPrimary: {
            '&:hover': {
              backgroundColor: fixedColors.primary.dark
            }
          },
          containedSecondary: {
            '&:hover': {
              backgroundColor: fixedColors.secondary.dark
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: fixedColors.primary.light,
            color: fixedColors.primary.dark
          },
          colorSecondary: {
            backgroundColor: fixedColors.secondary.light,
            color: fixedColors.secondary.dark
          },
          colorError: {
            backgroundColor: fixedColors.error.light,
            color: fixedColors.error.dark
          },
          colorSuccess: {
            backgroundColor: fixedColors.success.light,
            color: fixedColors.success.dark
          },
          colorInfo: {
            backgroundColor: fixedColors.info.light,
            color: fixedColors.info.dark
          },
          colorWarning: {
            backgroundColor: fixedColors.warning.light,
            color: fixedColors.warning.dark
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: fixedColors.success.light,
            color: fixedColors.success.dark
          },
          standardError: {
            backgroundColor: fixedColors.error.light,
            color: fixedColors.error.dark
          },
          standardWarning: {
            backgroundColor: fixedColors.warning.light,
            color: fixedColors.warning.dark
          },
          standardInfo: {
            backgroundColor: fixedColors.info.light,
            color: fixedColors.info.dark
          }
        }
      }
    }
  });
};

export default createAppTheme;
export { customColors };
